import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useBoard } from '../../hooks/useBoard';
import { BoardMessage } from '../../types/board';
import { generateBoardResponse } from '../../utils/boardAI';

interface BoardConversationProps {
  boardId: string;
  open: boolean;
  onClose: () => void;
}

export function BoardConversation({ boardId, open, onClose }: BoardConversationProps) {
  const { members, getConversationMessages, addMessage } = useBoard();
  const [messages, setMessages] = useState<BoardMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getConversationMessages(boardId);
      if (result.error) {
        setError(result.error);
      } else {
        setMessages(result.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [boardId, getConversationMessages]);

  useEffect(() => {
    if (open && boardId) {
      loadMessages();
    }
  }, [open, boardId, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || aiLoading) return;

    const userMessage: Omit<BoardMessage, 'id' | 'created_at'> = {
      conversation_id: boardId,
      speaker: 'You',
      content: newMessage.trim(),
      message_type: 'user_input',
    };

    try {
      setAiLoading(true);
      setError(null);

      // Add user message
      const userResult = await addMessage(boardId, userMessage);
      if (userResult.error) {
        throw new Error(userResult.error);
      }

      setMessages(prev => [...prev, userResult.data]);
      setNewMessage('');

      // Generate AI response
      const aiResponses = await generateBoardResponse(
        members,
        [...messages, userResult.data],
        newMessage.trim(),
        'Board consultation'
      );

      // Add AI responses
      for (const response of aiResponses) {
        const aiMessage: Omit<BoardMessage, 'id' | 'created_at'> = {
          conversation_id: boardId,
          speaker: response.speaker,
          content: response.content,
          message_type: response.message_type,
        };

        const aiResult = await addMessage(boardId, aiMessage);
        if (aiResult.error) {
          console.error('Failed to save AI message:', aiResult.error);
        } else {
          setMessages(prev => [...prev, aiResult.data]);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message');
    } finally {
      setAiLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getSpeakerAvatar = (speaker: string) => {
    if (speaker === 'You') {
      return <PersonIcon />;
    }
    
    const member = members.find(m => m.name === speaker);
    if (member) {
      return <Avatar sx={{ bgcolor: 'primary.main' }}>{member.name.charAt(0)}</Avatar>;
    }
    
    return <Avatar sx={{ bgcolor: 'secondary.main' }}>{speaker.charAt(0)}</Avatar>;
  };



  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Board Consultation
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Messages Area */}
        <Box sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Start a conversation with your board
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Ask for advice, discuss decisions, or get strategic guidance from your board members.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {messages.map((message, index) => (
                <Box
                  key={message.id || index}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'flex-start',
                    flexDirection: message.speaker === 'You' ? 'row-reverse' : 'row',
                  }}
                >
                  {getSpeakerAvatar(message.speaker)}
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '70%',
                      backgroundColor: message.speaker === 'You' ? 'primary.light' : 'background.paper',
                      color: message.speaker === 'You' ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {message.speaker}
                      </Typography>
                      {message.message_type !== 'user_input' && (
                        <Chip
                          label={message.message_type.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {message.content}
                    </Typography>
                  </Paper>
                </Box>
              ))}
              {aiLoading && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>A</Avatar>
                  <Paper sx={{ p: 2, backgroundColor: 'background.paper' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Assistant
                      </Typography>
                      <CircularProgress size={16} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Board members are deliberating...
                    </Typography>
                  </Paper>
                </Box>
              )}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Input Area */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            placeholder="Ask your board for advice..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={aiLoading}
          />
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || aiLoading}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            <SendIcon />
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
} 